import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Col, FormGroup, Input, Row, Table, Label, Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import Loading from '../../components/Loading/Loading';
import ProcessesDetail from '../../components/ProcessesDetail';
import paginationFactory from 'react-bootstrap-table2-paginator';
import moment from 'moment';
import axios from 'axios';
import { FLOW_NAME_ENUM, STEP_STATUS_ENUM, INITIATED_BY_ENUM } from './constants';
import { FLOW_NAME_TO_PROCESS_MAP, FLOW_NAME_TO_CHANGE_TYPE } from '../../constants/processes';

const FlowDataManagement = () => {
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [flowsTableData, setFlowsTableData] = useState([]);

  const [dataTableData, setDataTableData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  // Flow table state
  const [flowPage, setFlowPage] = useState(1);
  const [flowSize, setFlowSize] = useState(10);
  const [flowTotalSize, setFlowTotalSize] = useState(0);
  const [flowSortBy, setFlowSortBy] = useState('created');
  const [flowSortDir, setFlowSortDir] = useState('desc');

  // Data table state
  const [dataPage, setDataPage] = useState(1);
  const [dataSize, setDataSize] = useState(10);
  const [dataTotalSize, setDataTotalSize] = useState(0);
  const [dataSortBy, setDataSortBy] = useState('receivedAt');
  const [dataSortDir, setDataSortDir] = useState('desc');

  // Flow filters state
  const [flowFilters, setFlowFilters] = useState({
    flowName: '',
    flowStatus: '',
    initiatedBy: '',
    meterPoint: '',
  });

  // Data filters state
  const [dataFilters, setDataFilters] = useState({
    meterPoint: '',
  });

  // Debounced meter point inputs
  const [flowMeterPointInput, setFlowMeterPointInput] = useState('');
  const [dataMeterPointInput, setDataMeterPointInput] = useState('');
  const flowMeterPointTimer = useRef(null);
  const dataMeterPointTimer = useRef(null);

  // Modal state for retry process
  const [modalOpen, setModalOpen] = useState(false);
  const [processDefinitionId, setProcessDefinitionId] = useState(null);
  const [processInitialData, setProcessInitialData] = useState(null);

  const flowColumns = useMemo(
    () => [
      {
        dataField: 'name',
        text: 'Flow Name',
        sort: true,
      },
      {
        dataField: 'flowRef',
        text: 'Reference',
        sort: true,
      },
      {
        dataField: 'status',
        text: 'Status',
        sort: true,
      },
      {
        dataField: 'initiatedBy',
        text: 'Initiated By',
        sort: true,
        formatter: (cell) => INITIATED_BY_ENUM[cell] || cell || '',
      },
      {
        dataField: 'meterPoint',
        text: 'Meter Point',
        sort: true,
      },
      {
        dataField: 'rejectReason',
        text: 'Reject Reason',
        sort: true,
      },
      {
        dataField: 'actions',
        text: 'Actions',
        isDummyField: true,
        formatter: (cell, row) => {
          const flowNameKey = Object.keys(FLOW_NAME_ENUM).find((key) => FLOW_NAME_ENUM[key] === row.name);
          const processId = flowNameKey
              ? FLOW_NAME_TO_PROCESS_MAP[flowNameKey]
              : null;
          return (
            <div className='d-flex' style={{ gap: '8px' }}>
              <Button
                className='icon ion-funnel'
                size='sm'
                title='Apply filters from this row'
                onClick={(e) => {
                  e.stopPropagation();
                  const nameKey = Object.keys(FLOW_NAME_ENUM).find((key) => FLOW_NAME_ENUM[key] === row.name) || '';
                  const statusKey =
                    Object.keys(STEP_STATUS_ENUM).find((key) => STEP_STATUS_ENUM[key] === row.status) || '';
                  setFlowFilters({
                    flowName: nameKey,
                    flowStatus: statusKey,
                    initiatedBy: row.initiatedBy || '',
                    meterPoint: row.meterPoint || '',
                  });
                  setFlowMeterPointInput(row.meterPoint || '');
                  setFlowPage(1);
                }}
              ></Button>
              {processId && (row.status === 'Failed' || row.status === 'Interrupted') && (
                <Button
                  color='primary'
                  size='sm'
                  onClick={(e) => {
                    e.stopPropagation();
                    setProcessDefinitionId(processId);
                    const changeType = flowNameKey ? FLOW_NAME_TO_CHANGE_TYPE[flowNameKey] : null;
                    const initial = {};
                    if (changeType) initial.changeType = changeType;
                    if (row.meterPoint) initial.mpan_core = row.meterPoint;
                    setProcessInitialData(Object.keys(initial).length ? initial : null);
                    setModalOpen(true);
                  }}
                >
                  Retry
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const dataColumns = useMemo(
    () => [
      {
        dataField: 'type',
        text: 'Data Type',
        sort: true,
      },
      {
        dataField: 'content',
        text: 'Content',
        sort: true,
        formatter: (cell) => {
          try {
            return (
              <pre
                style={{
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  maxWidth: '100%',
                  maxHeight: '100px',
                  textAlign: 'left',
                }}
              >
                {JSON.stringify(cell, null, 2)}
              </pre>
            );
          } catch (e) {
            return cell;
          }
        },
      },
      {
        dataField: 'receiveDate',
        text: 'Date',
        formatter: (cell) => moment(cell).format('DD/MM/YYYY'),
        sort: true,
        sortValue: (cell) => moment(cell).valueOf(),
      },
      {
        dataField: 'meterPoint',
        text: 'Meter Point',
        sort: true,
      },
    ],
    []
  );

  const expandRow = {
    renderer: (row) => (
      <Row>
        <Col>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Date</th>
                <th>Data</th>
                <th>Status</th>
                <th>Reject Reason</th>
              </tr>
            </thead>
            <tbody>
              {row.steps.map((item, index) => (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td>{moment(item.date).format('DD/MM/YYYY')}</td>
                  <td>
                    <pre
                      style={{
                        whiteSpace: 'pre',
                        overflowX: 'auto',
                        maxWidth: '100%',
                        maxHeight: '100px',
                        textAlign: 'left',
                      }}
                    >
                      {JSON.stringify(item.data, null, 2)}
                    </pre>
                  </td>
                  <td>{item.status}</td>
                  <td>{item.rejectReason || ''}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    ),
    showExpandColumn: true,
    expandByColumnOnly: true,
  };

  // Fetch flows data from API
  const fetchFlows = async () => {
    setLoadingFlows(true);
    try {
      const token = localStorage.token;

      // Build query parameters
      const params = {
        page: flowPage - 1,
        size: flowSize,
        sortBy: flowSortBy,
        sortDir: flowSortDir,
      };

      // Add filters only if they have values
      Object.keys(flowFilters).forEach((key) => {
        if (flowFilters[key] && flowFilters[key].trim() !== '') {
          params[key] = flowFilters[key];
        }
      });

      const response = await axios.get('/df/management/flows', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setFlowsTableData(response.data.content || []);
      setFlowTotalSize(response.data.page.totalElements || 0);
    } catch (error) {
      setFlowsTableData([]);
      setFlowTotalSize(0);
    }
    setLoadingFlows(false);
  };

  // Fetch data from API
  const fetchData = async () => {
    setLoadingData(true);
    try {
      const token = localStorage.token;

      // Build query parameters
      const params = {
        page: dataPage - 1,
        size: dataSize,
        sortBy: dataSortBy,
        sortDir: dataSortDir,
      };

      // Add filters only if they have values
      Object.keys(dataFilters).forEach((key) => {
        if (dataFilters[key] && dataFilters[key].trim() !== '') {
          params[key] = dataFilters[key];
        }
      });

      const response = await axios.get('/df/management/meter-point-data', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDataTableData(response.data.content || []);
      setDataTotalSize(response.data.page.totalElements || 0);
    } catch (error) {
      setDataTableData([]);
      setDataTotalSize(0);
    }
    setLoadingData(false);
  };

  // Fetch data when page/size/sort/filters change
  useEffect(() => {
    fetchFlows();
  }, [flowPage, flowSize, flowSortBy, flowSortDir, flowFilters]);

  useEffect(() => {
    fetchData();
  }, [dataPage, dataSize, dataSortBy, dataSortDir, dataFilters]);

  // Flow filter change handler
  const handleFlowFilterChange = (filterName, value) => {
    setFlowFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
    setFlowPage(1); // Reset to first page on filter change
  };

  // Debounced flow meter point handler
  const handleFlowMeterPointChange = useCallback((value) => {
    setFlowMeterPointInput(value);
    if (flowMeterPointTimer.current) clearTimeout(flowMeterPointTimer.current);
    flowMeterPointTimer.current = setTimeout(() => {
      handleFlowFilterChange('meterPoint', value);
    }, 500);
  }, []);

  // Debounced data meter point handler
  const handleDataMeterPointChange = useCallback((value) => {
    setDataMeterPointInput(value);
    if (dataMeterPointTimer.current) clearTimeout(dataMeterPointTimer.current);
    dataMeterPointTimer.current = setTimeout(() => {
      handleDataFilterChange('meterPoint', value);
    }, 500);
  }, []);

  // Clear all flow filters
  const clearAllFlowFilters = () => {
    setFlowFilters({
      flowName: '',
      flowStatus: '',
      initiatedBy: '',
      meterPoint: '',
    });
    setFlowMeterPointInput('');
    setFlowPage(1);
  };

  // Render select filter helper
  const renderSelectFilter = (id, label, value, options) => (
    <FormGroup>
      <Label for={id}>{label}</Label>
      <Input type='select' id={id} value={value} onChange={(e) => handleFlowFilterChange(id, e.target.value)}>
        <option value=''>All</option>
        {Object.entries(options).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </Input>
    </FormGroup>
  );

  // Table change handlers
  const handleFlowTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === 'sort') {
      setFlowSortBy(sortField);
      setFlowSortDir(sortOrder);
    }
  };

  // Data filter change handler
  const handleDataFilterChange = (filterName, value) => {
    setDataFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
    setDataPage(1); // Reset to first page on filter change
  };

  // Render text filter helper
  const renderTextFilter = (id, label, value, onChange) => (
    <FormGroup>
      <Label for={id}>{label}</Label>
      <Input
        type='text'
        id={id}
        placeholder={`Enter ${label}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormGroup>
  );

  // Data table change handler
  const handleDataTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === 'sort') {
      setDataSortBy(sortField);
      setDataSortDir(sortOrder);
    }
  };

  return (
    <div className='organization'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Flow & Data Management</h1>
        </Col>
      </Row>

      <div className='page-cnt'>
        <Row className='mb-3'>
          <Col>
            <h3>Flow</h3>
          </Col>
        </Row>

        {/* Flow Filters Section */}
        <Row className='mb-4'>
          <Col md={3}>{renderSelectFilter('flowName', 'Flow Name', flowFilters.flowName, FLOW_NAME_ENUM)}</Col>
          <Col md={3}>{renderSelectFilter('flowStatus', 'Flow Status', flowFilters.flowStatus, STEP_STATUS_ENUM)}</Col>
          <Col md={3}>{renderSelectFilter('initiatedBy', 'Initiated By', flowFilters.initiatedBy, INITIATED_BY_ENUM)}</Col>
          <Col md={3}>
            <FormGroup>
              <Label for='meterPoint'>Meter Point</Label>
              <Input
                type='text'
                id='meterPoint'
                placeholder='Enter Meter Point'
                value={flowMeterPointInput}
                onChange={(e) => handleFlowMeterPointChange(e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col md={3} className='d-flex align-items-end'>
            <FormGroup>
              <Button color='primary' onClick={clearAllFlowFilters}>
                Clear All Filters
              </Button>
            </FormGroup>
          </Col>
        </Row>

        {/* Flow Table Section */}
        <Row>
          <Col className='col-12'>
            {loadingFlows && <Loading />}
            <BootstrapTable
              classes='table-hover'
              wrapperClasses='table-responsive'
              noDataIndication={() => <div>No records in table.</div>}
              columns={flowColumns}
              data={flowsTableData}
              expandRow={expandRow}
              keyField={'id'}
              bordered={false}
              hover
              remote={{
                filter: false,
                pagination: true,
                sort: true,
              }}
              pagination={paginationFactory({
                page: flowPage,
                sizePerPage: flowSize,
                totalSize: flowTotalSize,
                onPageChange: (page) => setFlowPage(page),
                onSizePerPageChange: (sizePerPage, page) => {
                  setFlowSize(sizePerPage);
                  setFlowPage(page);
                },
                sizePerPageList: [
                  { text: '5', value: 5 },
                  { text: '10', value: 10 },
                  { text: '20', value: 20 },
                ],
              })}
              onTableChange={handleFlowTableChange}
            />
          </Col>
        </Row>
        <Row className='mt-4 mb-3'>
          <Col>
            <h3>Data</h3>
          </Col>
        </Row>

        {/* Data Filters Section */}
        <Row className='mb-4'>
          <Col md={3}>{renderTextFilter('meterPoint', 'Meter Point', dataMeterPointInput, handleDataMeterPointChange)}</Col>
        </Row>

        {/* Data Table Section */}
        <Row>
          <Col className='col-12'>
            {loadingData && <Loading />}
            <BootstrapTable
              classes='table-hover'
              wrapperClasses='table-responsive'
              noDataIndication={() => <div>No records in table.</div>}
              columns={dataColumns}
              data={dataTableData}
              keyField={'id'}
              bordered={false}
              hover
              remote={{
                filter: false,
                pagination: true,
                sort: true,
              }}
              pagination={paginationFactory({
                page: dataPage,
                sizePerPage: dataSize,
                totalSize: dataTotalSize,
                onPageChange: (page) => setDataPage(page),
                onSizePerPageChange: (sizePerPage, page) => {
                  setDataSize(sizePerPage);
                  setDataPage(page);
                },
                sizePerPageList: [
                  { text: '5', value: 5 },
                  { text: '10', value: 10 },
                  { text: '20', value: 20 },
                ],
              })}
              onTableChange={handleDataTableChange}
            />
          </Col>
        </Row>
      </div>
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} style={{ maxWidth: '90%' }}>
        <ModalHeader toggle={() => setModalOpen(false)}></ModalHeader>
        <ModalBody>
          <ProcessesDetail onClose={() => setModalOpen(false)} processDefinitionId={processDefinitionId} initialData={processInitialData} />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default FlowDataManagement;

